import type { SortingState } from "@tanstack/react-table";
import { t } from "ttag";

import { TimezoneIndicator } from "metabase/transforms/components/TimezoneIndicator";
import {
  formatRunMethod,
  formatStatus,
  getRunDurationMs,
  getTransformRunName,
  isErrorStatus,
  parseTimestampWithTimezone,
} from "metabase/transforms/utils";
import type { TreeTableColumnDef } from "metabase/ui";
import {
  Box,
  Ellipsified,
  Group,
  SortableHeaderPill,
  Text,
  Tooltip,
} from "metabase/ui";
import { EMPTY_CELL_PLACEHOLDER } from "metabase/utils/constants";
import { formatDurationLong } from "metabase/utils/formatting";
import {
  TRANSFORM_RUN_SORT_COLUMNS,
  type TransformRun,
  type TransformRunSortColumn,
  type TransformTag,
  type TransformTagId,
} from "metabase-types/api";

import type { TransformRunSortOptions } from "../types";

import { TagList } from "./TagList";

function getTransformColumn(): TreeTableColumnDef<TransformRun> {
  return {
    id: "transform-name" satisfies TransformRunSortColumn,
    header: t`Transform`,
    width: "auto",
    maxAutoWidth: 520,
    enableSorting: true,
    accessorFn: (run) => {
      return getTransformRunName(run);
    },
    cell: ({ row, getValue }) => {
      const run = row.original;
      const value = String(getValue());
      const isTransformDeleted = run.transform?.deleted === true;

      return (
        <Ellipsified>
          {isTransformDeleted ? (
            <Tooltip label={t`${value} has been deleted`}>
              <Text
                c="text-disabled"
                component="span"
                display="inline"
                fs="italic"
              >
                {value}
              </Text>
            </Tooltip>
          ) : (
            value
          )}
        </Ellipsified>
      );
    },
  };
}

function getStartedAtColumn(
  systemTimezone: string | undefined,
): TreeTableColumnDef<TransformRun> {
  return {
    id: "start-time" satisfies TransformRunSortColumn,
    header: ({ header }) => (
      <Group gap="xs" wrap="nowrap">
        <SortableHeaderPill
          name={t`Started at`}
          sort={header.column.getIsSorted() || undefined}
        />
        <TimezoneIndicator />
      </Group>
    ),
    width: "auto",
    enableSorting: true,
    accessorFn: (run) => {
      return parseTimestampWithTimezone(run.start_time, systemTimezone).format(
        "lll",
      );
    },
    cell: ({ getValue }) => {
      const value = getValue();
      return <Ellipsified>{String(value)}</Ellipsified>;
    },
  };
}

function getEndedAtColumn(
  systemTimezone: string | undefined,
): TreeTableColumnDef<TransformRun> {
  return {
    id: "end-time" satisfies TransformRunSortColumn,
    header: ({ header }) => (
      <Group gap="xs" wrap="nowrap">
        <SortableHeaderPill
          name={t`Ended at`}
          sort={header.column.getIsSorted() || undefined}
        />
        <TimezoneIndicator />
      </Group>
    ),
    width: "auto",
    enableSorting: true,
    accessorFn: (run) => {
      return run.end_time != null
        ? parseTimestampWithTimezone(run.end_time, systemTimezone).format("lll")
        : null;
    },
    cell: ({ getValue }) => {
      const value = getValue<string | null>();
      if (value == null) {
        return EMPTY_CELL_PLACEHOLDER;
      }
      return <Ellipsified>{String(value)}</Ellipsified>;
    },
  };
}

function getDurationColumn(): TreeTableColumnDef<TransformRun> {
  return {
    id: "duration" satisfies TransformRunSortColumn,
    header: ({ header }) => (
      <SortableHeaderPill
        name={t`Duration`}
        sort={header.column.getIsSorted() || undefined}
      />
    ),
    width: 120,
    enableSorting: true,
    accessorFn: (run) => getRunDurationMs(run),
    cell: ({ getValue }) => {
      const ms = getValue<number | null>();
      if (ms == null) {
        return EMPTY_CELL_PLACEHOLDER;
      }
      return <Ellipsified>{formatDurationLong(ms)}</Ellipsified>;
    },
  };
}

function getStatusColumn(): TreeTableColumnDef<TransformRun> {
  return {
    id: "status" satisfies TransformRunSortColumn,
    header: t`Status`,
    width: 100,
    enableSorting: true,
    accessorFn: (row) => formatStatus(row.status),
    cell: ({ row }) => {
      const { status } = row.original;
      return (
        <Box c={isErrorStatus(status) ? "feedback-negative" : undefined}>
          {formatStatus(status)}
        </Box>
      );
    },
  };
}

function getRunMethodColumn(): TreeTableColumnDef<TransformRun> {
  return {
    id: "run-method" satisfies TransformRunSortColumn,
    header: t`Trigger`,
    width: "auto",
    enableSorting: true,
    accessorFn: (row) => formatRunMethod(row.run_method),
    cell: ({ getValue }) => {
      const value = String(getValue());
      return <Ellipsified>{value}</Ellipsified>;
    },
  };
}

function getTagById(
  tags: TransformTag[],
): Record<TransformTagId, TransformTag> {
  return Object.fromEntries(tags.map((tag) => [tag.id, tag]));
}

function getTagList(
  tagIds: TransformTagId[],
  tagById: Record<TransformTagId, TransformTag>,
) {
  return tagIds.map((tagId) => tagById[tagId]).filter((tag) => tag != null);
}

function getTagsLabel(tags: TransformTag[]) {
  return tags.map((tag) => tag.name).join(", ");
}

function getTransformTagsColumn(
  tagsById: Record<TransformTagId, TransformTag>,
): TreeTableColumnDef<TransformRun> {
  return {
    id: "transform-tags" satisfies TransformRunSortColumn,
    header: t`Tags`,
    width: "auto",
    enableSorting: true,
    accessorFn: (row) =>
      getTagsLabel(getTagList(row.transform?.tag_ids ?? [], tagsById)),
    cell: ({ row }) => {
      const run = row.original;
      const tags = getTagList(run.transform?.tag_ids ?? [], tagsById);
      return <TagList tags={tags} />;
    },
  };
}

export function getColumns(
  tags: TransformTag[],
  systemTimezone: string | undefined,
): TreeTableColumnDef<TransformRun>[] {
  const tagsById = getTagById(tags);

  return [
    getTransformColumn(),
    getStartedAtColumn(systemTimezone),
    getEndedAtColumn(systemTimezone),
    getDurationColumn(),
    getStatusColumn(),
    getRunMethodColumn(),
    getTransformTagsColumn(tagsById),
  ];
}

export function getSortingState(
  sortOptions: TransformRunSortOptions | undefined,
): SortingState {
  return sortOptions != null
    ? [{ id: sortOptions.column, desc: sortOptions.direction === "desc" }]
    : [];
}

export function getSortingOptions(
  sortingState: SortingState,
): TransformRunSortOptions | undefined {
  if (sortingState.length === 0) {
    return undefined;
  }

  const { id, desc } = sortingState[0];
  const column = TRANSFORM_RUN_SORT_COLUMNS.find((column) => column === id);
  if (column == null) {
    return undefined;
  }

  return { column, direction: desc ? "desc" : "asc" };
}
